import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/sektorler/ihracat';
const pageUrl = `${siteUrl}${pagePath}`;

const ihracatFAQItems = [
  {
    question: 'İhracat pazarlama ile yurt içi pazarlama arasındaki fark nedir?',
    answer:
      'Yurt içinde markanızı tanıyan bir kitle vardır. İhracatta sıfırdan başlarsınız. Pazarı tanımıyorsunuz, rakipler farklı, satın alma alışkanlıkları farklı, arama davranışı farklı. İhracat pazarlama; pazar seçimi, lokalizasyon, kültürel uyum ve yerel güven inşası gerektirir. Ürünü göndermek başka, o pazarda satmak başka iştir.',
  },
  {
    question: 'E-ihracata nereden başlamalıyım?',
    answer:
      'Önce pazar seçimiyle. Her ürün her pazarda satılmaz. Talep, rekabet, lojistik maliyeti, gümrük ve ödeme altyapısını analiz ederek 1-2 pilot pazar belirleriz. Sonra markayı o pazara göre konumlandırır, Amazon Global veya yerel pazaryerleriyle operasyonu kurarız.',
  },
  {
    question: 'Hangi pazarlar e-ihracat için daha uygundur?',
    answer:
      'Ürüne ve kategoriye göre değişir. Almanya, İngiltere ve ABD yüksek hacimli ama rekabetin yoğun olduğu pazarlardır. Körfez, Orta Doğu ve Balkanlar ise doğru ürünle daha düşük rekabetle girilebilen pazarlardır. Biz pazar seçimini veriyle yapıyor, tahminle değil.',
  },
  {
    question: 'Uluslararası SEO neden kritik?',
    answer:
      'Hedef pazardaki müşteri, ürünü kendi dilinde arar. Yerel dilde içerik, yerel domain yapısı, yerel arama motorları ve ülkeye özel anahtar kelimeler olmadan organik görünürlük sağlayamazsınız. Uluslararası SEO, ihracatta sürdürülebilir büyümenin temelidir.',
  },
  {
    question: 'İhracat pazarlamasında bütçe nasıl planlanır?',
    answer:
      'Tüm pazarlara aynı anda girmek yerine, seçilen pilot pazara yoğunlaşırız. Bütçeyi pazar araştırması, lokalizasyon, reklam ve operasyon arasında dengeler; ilk satışlar geldikçe ölçeği büyütürüz. Küçük başlayıp veriyle büyümek, ihracatta en doğru yoldur.',
  },
  {
    question: 'İhracatta marka konumlandırması neden önemli?',
    answer:
      'Yurt dışında markanızı kimse tanımıyor. Yalnızca ürünle satarsanız fiyat rekabetine girer, kâr marjınızı kaybedersiniz. Doğru konumlandırma, sizi o pazarda bir anlam ifade eden marka yapar. İhracatta asıl rekabet üründe değil, markanın nasıl algılandığındadır.',
  },
];

const faqSchema = getFAQSchema(ihracatFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'İhracat Dijital Pazarlama Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'İhracatçı markalar için pazar seçimi, uluslararası SEO, e-ihracat ve marka konumlandırmasıyla yurt dışında sürdürülebilir büyüme. BIFF Digital ihracat pazarlamasını veri ve stratejiyle yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'İhracat Dijital Pazarlama',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'İhracat Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pazar Seçimi & Araştırma', description: 'Veri odaklı hedef pazar analizi ve seçimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Uluslararası SEO', description: 'Yerel dilde organik görünürlük ve otorite inşası' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'E-İhracat & Pazaryerleri', description: 'Amazon Global ve yerel pazaryerleri yönetimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Marka Konumlandırma', description: 'Hedef pazarda doğru algı ve güven inşası' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Sektörler', item: `${siteUrl}/sektorler/ihracat` },
        { '@type': 'ListItem', position: 3, name: 'İhracat', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function Ihracat() {
  useEffect(() => {
    document.title = 'İhracat Dijital Pazarlama Ajansı | E-İhracat & Global Büyüme | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Aerial%20cinematic%20view%20of%20a%20massive%20cargo%20container%20port%20at%20dusk%20with%20stacked%20shipping%20containers%20and%20cranes%20loading%20a%20container%20ship%2C%20global%20trade%20and%20export%20concept%2C%20dark%20moody%20atmosphere%20with%20crimson%20red%20accent%20lighting%2C%20deep%20charcoal%20and%20red%20tones%2C%20premium%20editorial%20photography%2C%20dramatic%20lighting%2C%20no%20people%2C%20wide%20angle&width=1440&height=800&seq=ihracat-hero-01&orientation=landscape"
              alt="İhracat dijital pazarlama - global ticaret ve e-ihracat büyüme stratejisi"
              title="İhracat Pazarlama - BIFF Digital"
              width="1440"
              height="800"
              className="w-full h-full object-cover object-center"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-[0.2em] uppercase mb-4">SEKTÖRLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-6">
              İhracat
            </h1>
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-4">
              Sınırlar Ürünü Değil,<br />
              <span className="text-primary-500">Markayı Test Eder.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-2xl speakable-summary">
              İhracatta asıl mesele ürünü yurt dışına göndermek değil, markayı o pazarda satılır kılmaktır. Pazar seçimi, lokalizasyon ve güven inşası — biz ihracatçı markaların yurt dışında sıfırdan var olmasını yönetiyoruz.
            </p>
          </div>
        </section>

        {/* Ürünü Göndermek vs Satmak */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Ürünü Yurt Dışına Göndermekle,<br />Yurt Dışında Satmak Aynı Şey Değildir.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir ürünü konteynere koyup göndermek operasyondur. O ürünü hedef pazarda arayan, güvenen ve satın alan bir müşteriye ulaştırmak ise pazarlamadır. İhracatta çoğu marka ilkini çözer, ikincisinde takılır.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Yurt dışında kimse sizi tanımıyor.
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                Bu bir sorun değil, bir fırsat. Markanızı o pazarda doğru konumlandırırsanız, doğduğunuz andan itibaren doğru algılanırsınız.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Sıfırdan Global Markaya<br />Uçtan Uca İhracat Pazarlaması
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-global-line', title: 'Pazar Seçimi & Araştırma', desc: 'Hangi pazara girileceğini tahminle değil, veriyle belirliyoruz. Talep hacmi, rekabet yoğunluğu, lojistik maliyeti ve gümrük altyapısını analiz ederek pilot pazar seçiyoruz.' },
                { icon: 'ri-search-line', title: 'Uluslararası SEO', desc: 'Hedef pazardaki müşteri kendi dilinde arar. Yerel dilde içerik, ülkeye özel domain yapısı ve yerel anahtar kelimelerle organik görünürlük inşa ediyoruz.' },
                { icon: 'ri-store-3-line', title: 'E-İhracat & Pazaryerleri', desc: 'Amazon Global, yerel pazaryerleri ve markanın kendi sitesi üzerinden çok kanallı bir satış sistemi kuruyoruz. Ürün beslemeleri, fiyatlandırma ve stok yönetimini birlikte kurguluyoruz.' },
                { icon: 'ri-translate-2', title: 'Lokalizasyon & Kültürel Uyum', desc: 'Sadece çeviri değil, kültürel uyum. Mesajın o pazarda doğru anlaşılmasını sağlıyoruz. Almanya\'da satan bir metin, İngiltere\'de aynı etkiyi yaratmayabilir.' },
                { icon: 'ri-megaphone-line', title: 'Global Dijital Reklam', desc: 'Google Ads ve Meta Ads\'i hedef pazara göre lokalize ediyoruz. Doğru ülkede, doğru dilde, doğru mesajla görünürlük sağlıyoruz. Reklam bütçesini ROAS odaklı yönetiyoruz.' },
                { icon: 'ri-award-line', title: 'Marka Konumlandırma', desc: 'Ürünle satmak fiyat rekabetine iter. Biz markanızı hedef pazarda bir anlam ifade eden konuma taşıyor, güven ve otorite inşa ediyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              İhracat Yolculuğunuz
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Pazar Analizi', desc: 'Ürününüzü, kategorinizi ve hedef pazarları analiz ediyoruz. Nerede talep var, nerede rekabet açığı var — veriyle görüyoruz.' },
                { step: '02', title: 'Konumlandırma & Mesaj', desc: 'Seçilen pazarda markanızın ne anlama geleceğini tanımlıyoruz. Konumlandırma, mesaj ve lokalize değer önerisi netleşiyor.' },
                { step: '03', title: 'Kanal & Operasyon Kurulumu', desc: 'Pazaryerleri, kendi siteniz, uluslararası SEO ve reklam kanallarını kuruyoruz. Operasyonun tamamı satışa hazır hale geliyor.' },
                { step: '04', title: 'Ölçüm & Ölçekleme', desc: 'İlk satışlar geldikçe veriyi okuyor, çalışan kanallara yatırım yapıyor ve yeni pazarlara açılımı planlıyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={ihracatFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="İHRACAT"
          sectionSummary="İhracat dijital pazarlama hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  İhracatta kaybettiğiniz pazarı<br />
                  <span className="text-primary-500">birlikte bulalım.</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Hangi pazara girmelisiniz, nerede rekabet açığı var, markanızı nasıl konumlandırmalısınız — veriyle görelim.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Ücretsiz Pazar Analizi
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}